'use client';

import type { ComponentProps, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { entrance } from '@/src/shared/lib/chart/entrance';

type AxisTextProps = Omit<ComponentProps<'text'>, 'fontSize' | 'fontWeight'> & {
  children: ReactNode;
};

/**
 * An axis or value label inside a chart: 12 px, muted, with a halo in the card's
 * colour so it stays readable over a line or an area. Screen readers skip it; the
 * chart's own label says the figures.
 */
export function AxisText({ className, children, ...props }: AxisTextProps) {
  return (
    <text
      fontSize={12}
      fontWeight={500}
      stroke="hsl(var(--card))"
      strokeWidth={3}
      paintOrder="stroke"
      aria-hidden="true"
      className={className ?? 'fill-muted-foreground tabular-nums'}
      {...props}
    >
      {children}
    </text>
  );
}

type DrawnPathProps = {
  d: string;
  stroke: string;
  strokeWidth?: number;
  dash?: string;
  reduced: boolean;
  delay?: number;
  duration?: number;
};

/**
 * A line that draws itself once when it enters, or starts drawn with reduced motion.
 * A dashed one is uncovered instead, so its dash pattern survives.
 */
export function DrawnPath({
  d,
  stroke,
  strokeWidth = 2,
  dash,
  reduced,
  delay,
  duration,
}: DrawnPathProps) {
  return (
    <motion.path
      d={d}
      fill="none"
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeDasharray={dash}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...entrance(dash ? 'reveal' : 'draw', { reduced, delay, duration })}
    />
  );
}

type FadeAreaProps = {
  d: string;
  fill: string;
  reduced: boolean;
  delay?: number;
  duration?: number;
};

/** The area under a line, fading in after the line has drawn. */
export function FadeArea({ d, fill, reduced, delay, duration }: FadeAreaProps) {
  return (
    <motion.path
      d={d}
      fill={fill}
      stroke="none"
      {...entrance('fade', { reduced, delay, duration })}
    />
  );
}
